import { inject, Injectable } from '@angular/core';
import { getCurrentUser, selectUser } from '@fantaz/shared/auth';
import {
  AuthServiceInjectionToken,
  GameHistoryService,
  TournamentApiService,
} from '@fantaz/shared/data-access/api';
import { CasualTournamentDetails } from '@fantaz/shared/data-access/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import {
  loadCasualTournamentDetails,
  loadCasualTournamentDetailsFailure,
  loadCasualTournamentDetailsSuccess,
  loadCasualTournamentRating,
  loadCasualTournamentRatingSuccess,
  timesUserTournamentPlayed,
  timesUserTournamentPlayedFailure,
  timesUserTournamentPlayedSuccess,
} from './casual-tournament-details.actions';
@Injectable()
export class CasualTournamentDetailsEffects {
  #authService = inject(AuthServiceInjectionToken);
  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadCasualTournamentDetails),
        switchMap(({ url }) => {
          return this.tournamentApiService.getTournamentDetails(url).pipe(
            switchMap((data: CasualTournamentDetails) => {
              return [
                loadCasualTournamentDetailsSuccess({
                  casualTournamentDetails: data,
                }),
              ];
            }),
            catchError((error) => {
              return of(
                loadCasualTournamentDetailsFailure({
                  error,
                }),
              );
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  /**
   * @deprecated
   */
  getGameRating$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadCasualTournamentRating),
        withLatestFrom(this.store.select(selectUser)),
        switchMap(([{ url }, user]) => {
          const params = { url, userId: user?.uuid };

          return this.tournamentApiService.getGameRating(params).pipe(
            map((data) => {
              return loadCasualTournamentRatingSuccess({
                casualTournamentRating: data,
              });
            }),
            catchError((error) => {
              return of(
                loadCasualTournamentDetailsFailure({
                  error,
                }),
              );
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  getTimesUserTournamentPlayed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(timesUserTournamentPlayed),
        withLatestFrom(this.store.select(getCurrentUser)),
        switchMap(
          ([{ eventPrototypeUuid, eventScheduleUuid }, currentUser]) => {
            if (currentUser) {
              return this.gameHistory
                .getTimesUserPlayed(eventPrototypeUuid, eventScheduleUuid)
                .pipe(
                  map((data) => {
                    return timesUserTournamentPlayedSuccess({
                      timesPlayed: data,
                    });
                  }),
                  catchError((error) => {
                    return of(
                      timesUserTournamentPlayedFailure({
                        error,
                      }),
                    );
                  }),
                );
            } else {
              return [];
            }
          },
        ),
      ),
    { dispatch: true },
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private tournamentApiService: TournamentApiService,
    private gameHistory: GameHistoryService,
  ) {}
}
